import React, { useMemo, useState } from 'react';
import { Badge, Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { ArrowRight, LinkSimple, Warning } from 'phosphor-react';

import { GlassCard } from '../../../../../../components/glass';
import { useContaCilMemoria } from '../../../../../../hooks/useContaCilEmpresas';
import { corConfianca, PocLadoBadge } from '../../conciliar/shared';
import { Callout } from '../../plano-contas/ui';

type Filtro = 'todas' | 'baixa';

export function AbaMemoria({ empresaId }: { empresaId: number }) {
  const { data, isLoading } = useContaCilMemoria(empresaId);
  const [filtro, setFiltro] = useState<Filtro>('todas');

  const itens = useMemo(() => {
    const arr = data?.itens ?? [];
    if (filtro === 'baixa') return arr.filter(i => i.conflito || i.pureza < 0.6);
    return arr;
  }, [data, filtro]);

  if (isLoading) {
    return (
      <Flex justify="center" py={10}>
        <Spinner color="#0EA5E9" thickness="2px" />
      </Flex>
    );
  }

  const resumo = data?.resumo;

  return (
    <Box>
      <GlassCard intensity="medium" p={5}>
        <Flex align="center" justify="space-between" gap={3} mb={3} wrap="wrap">
          <Box>
            <Text fontSize="14px" fontWeight="700" color="#0F172A">
              Memória aprendida
            </Text>
            <Text fontSize="11px" color="#94A3B8">
              {resumo?.total ?? 0} associações · {resumo?.confiavel ?? 0} confiáveis ·{' '}
              {resumo?.conflito ?? 0} em conflito
            </Text>
          </Box>
          <Flex
            display="inline-flex"
            gap="2px"
            p="4px"
            borderRadius="full"
            bg="rgba(255,255,255,0.72)"
            borderWidth="1px"
            borderColor="rgba(255,255,255,0.6)"
          >
            {(
              [
                { id: 'todas', label: 'Todas' },
                { id: 'baixa', label: 'Pureza baixa' },
              ] as { id: Filtro; label: string }[]
            ).map(f => {
              const on = filtro === f.id;
              return (
                <Box
                  key={f.id}
                  as="button"
                  onClick={() => setFiltro(f.id)}
                  px={3}
                  py={1.5}
                  borderRadius="full"
                  fontSize="12px"
                  fontWeight="600"
                  color={on ? '#0EA5E9' : '#64748B'}
                  bg={on ? 'white' : 'transparent'}
                  boxShadow={on ? '0 2px 8px -2px rgba(15,23,42,0.12)' : 'none'}
                >
                  {f.label}
                </Box>
              );
            })}
          </Flex>
        </Flex>

        <Callout tone="info" titulo="Como ler">
          A IA lembra para onde cada contraparte costuma ir. <strong>Pureza alta</strong> = quase
          sempre a mesma conta; <strong>baixa</strong> = ambígua (vale revisar). O número é o{' '}
          <strong>suporte</strong> (quantas vezes foi observada).
        </Callout>

        {itens.length === 0 ? (
          <Box mt={3}>
            <Callout tone="sucesso" titulo="Sem padrões">
              {data?.itens?.length
                ? 'Nenhuma associação corresponde ao filtro.'
                : 'A IA ainda não aprendeu padrões — concilie (manual ou por IA) para alimentar a memória.'}
            </Callout>
          </Box>
        ) : (
          <Flex direction="column" gap={2} mt={3}>
            {itens.map(m => {
              const corPureza = corConfianca(m.pureza * 100);
              return (
                <Flex
                  key={`${m.contraparte}-${m.dc}`}
                  align="center"
                  gap={2}
                  px={3}
                  py={2.5}
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor="rgba(15,23,42,0.06)"
                  bg="rgba(255,255,255,0.5)"
                >
                  <PocLadoBadge lado={m.dc} />
                  <Text
                    fontSize="12px"
                    fontWeight="600"
                    color="#0F172A"
                    isTruncated
                    maxW="200px"
                    title={m.contraparte}
                  >
                    {m.contraparte}
                  </Text>
                  <Box color="#CBD5E1" flexShrink={0}>
                    <ArrowRight size={12} weight="bold" />
                  </Box>
                  <Flex align="center" gap={1} flexShrink={0}>
                    <Box color="#6366F1">
                      <LinkSimple size={12} weight="bold" />
                    </Box>
                    <Text fontSize="12px" fontWeight="600" color="#4338CA" fontFamily="mono">
                      {m.poc_codigo}
                    </Text>
                  </Flex>
                  {m.conflito && (
                    <Box
                      color="#DC2626"
                      flexShrink={0}
                      title="Conflito: razão diverge da coerência contábil"
                    >
                      <Warning size={13} weight="fill" />
                    </Box>
                  )}

                  <Box flex={1} minW="40px" mx={1}>
                    <Box h="6px" borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
                      <Box
                        h="full"
                        w={`${Math.round(m.pureza * 100)}%`}
                        borderRadius="full"
                        bg={corPureza}
                      />
                    </Box>
                  </Box>
                  <Text
                    fontSize="11px"
                    fontWeight="700"
                    color={corPureza}
                    minW="36px"
                    textAlign="right"
                    flexShrink={0}
                  >
                    {Math.round(m.pureza * 100)}%
                  </Text>
                  <Text
                    fontSize="11px"
                    color="#94A3B8"
                    minW="32px"
                    textAlign="right"
                    flexShrink={0}
                  >
                    {m.suporte}×
                  </Text>
                  {m.status === 'confiavel' ? (
                    <Badge colorScheme="green" borderRadius="full" fontSize="9px" flexShrink={0}>
                      confiável
                    </Badge>
                  ) : (
                    <Badge colorScheme="gray" borderRadius="full" fontSize="9px" flexShrink={0}>
                      quarentena
                    </Badge>
                  )}
                </Flex>
              );
            })}
          </Flex>
        )}
      </GlassCard>
    </Box>
  );
}
